export interface PageServiceAreaProps {
  heading: string;
  subheading?: string;
  intro?: string;
  areas: string[];
  address: string;
  hours: string;
  phone: string;
  mapEmbed: string;
}

export default function PageServiceArea({
  heading,
  subheading,
  intro,
  areas,
  address,
  hours,
  phone,
  mapEmbed,
}: PageServiceAreaProps) {
  return (
    <section className="bg-ivory py-24 lg:py-32">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-12">
        {/* Section header */}
        <div className="mb-12">
          {subheading && (
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-antique" />
              <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">{subheading}</span>
            </div>
          )}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2
              className="font-serif text-inkblack"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.015em' }}
            >
              {heading}
            </h2>
            {intro && (
              <p className="text-pewter text-sm font-sans font-300 leading-relaxed max-w-md lg:text-right">{intro}</p>
            )}
          </div>
        </div>

        {/* Main grid: Map + Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map — takes 2/3 width */}
          <div className="lg:col-span-2">
            <div className="w-full h-80 lg:h-[520px] overflow-hidden">
              <iframe
                src={mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Yarnell Emporium location map"
              />
            </div>
          </div>

          {/* Info panel — takes 1/3 width */}
          <div className="flex flex-col gap-0">
            {/* Address block */}
            <div className="bg-inkblack p-7 flex-1 flex flex-col justify-between">
              <div>
                <p className="text-antique text-xs font-sans font-600 tracking-[0.22em] uppercase mb-5">Store Details</p>
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="text-ivory/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-1.5">Address</p>
                    <p className="text-ivory text-sm font-sans font-300 leading-relaxed">{address}</p>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-antique text-xs font-sans font-600 tracking-[0.12em] uppercase hover:text-white transition-colors cursor-pointer"
                    >
                      Get Directions <i className="ri-arrow-right-line" />
                    </a>
                  </div>
                  <div>
                    <p className="text-ivory/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-1.5">Hours</p>
                    <p className="text-ivory text-sm font-sans font-300 leading-relaxed whitespace-pre-line">{hours}</p>
                  </div>
                  <div>
                    <p className="text-ivory/30 text-[10px] font-sans tracking-[0.2em] uppercase mb-1.5">Phone</p>
                    <a
                      href={`tel:${phone.replace(/[^0-9]/g, '')}`}
                      className="text-ivory text-sm font-sans font-300 hover:text-antique transition-colors cursor-pointer"
                    >
                      {phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Serving block */}
            <div className="bg-parchment border border-mist p-7">
              <p className="text-inkblack text-xs font-sans font-600 tracking-[0.22em] uppercase mb-4">Serving</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {areas.map((area, i) => (
                  <span
                    key={i}
                    className="text-xs font-sans font-500 text-inkblack border border-mist bg-ivory px-3 py-1.5 tracking-wide"
                  >
                    {area}
                  </span>
                ))}
              </div>
              {/* Drive times */}
              <div className="flex flex-col gap-2 border-t border-mist pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-pewter text-xs font-sans">From Prescott</span>
                  <span className="text-inkblack text-xs font-sans font-600">~40 min</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-pewter text-xs font-sans">From Wickenburg</span>
                  <span className="text-inkblack text-xs font-sans font-600">~45 min</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-pewter text-xs font-sans">From Phoenix</span>
                  <span className="text-inkblack text-xs font-sans font-600">~90 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
